import React, { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useFirebaseAuth } from '../context/FirebaseAuthContext';
import { useUserPersona } from '../context/UserPersonaContext';
import { useSidebar } from '../context/SidebarContext';
import { useImpersonation } from '../context/ImpersonationContext';
import { getApiBaseUrlWithFallback, getDomainBranding, shouldUseDefaultBranding } from '../config/environment';
import {
  Box,
  useTheme,
  useMediaQuery,
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Collapse,
  Divider,
  Button,
  Typography,
} from '@mui/material';
import {
  Home as HomeIcon,
  EmojiEvents as QualifiersIcon,
  SportsSoccer as CompetitionsIcon,
  Person as PersonIcon,
  PersonAdd as PersonAddIcon,
  HowToReg as HowToRegIcon,
  AdminPanelSettings as AdminIcon,
  ExpandLess,
  ExpandMore,
  ChevronLeft,
  ChevronRight,
  Settings as SettingsIcon,
  Group as GroupIcon,
  Assessment as AssessmentIcon,
  Storage as StorageIcon,
  Upload as UploadIcon,
  Search as SearchIcon,
  Gavel as RulesIcon,
  VerifiedUser as RoleApprovalsIcon,
  ExitToApp as ExitIcon,
} from '@mui/icons-material';

export default function Toolbar() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const navigate = useNavigate();
  const location = useLocation();
  const { user, isAdmin, isApprovedInterclubAdmin, adminStatusLoading, userProfile } = useFirebaseAuth();
  const { getNavigationLabels } = useUserPersona();
  const { sidebarOpen, toggleSidebar } = useSidebar();
  const { isImpersonating, impersonatedUser, stopImpersonation } = useImpersonation();
  
  const [adminMenuOpen, setAdminMenuOpen] = useState(false);
  const [seriesMenuOpen, setSeriesMenuOpen] = useState(true);
  const [interclubMenuOpen, setInterclubMenuOpen] = useState(true);
  const [brandingTitle, setBrandingTitle] = useState('');
  const [brandingLogoUrl, setBrandingLogoUrl] = useState('');
  const [qualifiersEnabled, setQualifiersEnabled] = useState(false);

  // Check if user has app admin access (from adminUsers collection)
  const hasAppAdminAccess = !adminStatusLoading && user && isAdmin();
  
  // Check if user has approved interclub admin access (from role approval system)
  const hasInterclubAdminAccess = !adminStatusLoading && user && isApprovedInterclubAdmin();
  
  // Debug logging for admin access
  useEffect(() => {
    console.log('🔧 Toolbar admin access:', {
      isImpersonating,
      impersonatedUser: impersonatedUser?.email,
      adminStatusLoading,
      hasAppAdminAccess,
      hasInterclubAdminAccess,
      userProfileEmail: userProfile?.email,
      userProfileRoles: userProfile?.roles,
      userProfileApproval: userProfile?.approvalStatus,
    });
  }, [isImpersonating, impersonatedUser, adminStatusLoading, hasAppAdminAccess, hasInterclubAdminAccess, userProfile]);
  
  // Combined admin access for showing qualifier link
  const hasAnyAdminAccess = hasAppAdminAccess || hasInterclubAdminAccess;
  
  // Show qualifier link only if admin is logged in OR if the interclub has qualifiers enabled
  const shouldShowQualifierLink = hasAnyAdminAccess || qualifiersEnabled;

  // Get dynamic labels and persona info
  const { persona, skaters: userSkaters, profile: personaProfile } = useUserPersona();
  const navLabels = getNavigationLabels();
  
  // Check if user has skaters, is a parent, or is an APPROVED coach - these users need access to skater management
  const hasSkaters = userSkaters && userSkaters.length > 0;
  // Coach must be approved by interclub/club admin to get coach privileges
  const isApprovedCoach = (personaProfile?.isCoach || personaProfile?.roles?.includes('coach')) && 
    personaProfile?.approvalStatus?.coach === 'approved';
  const isParentOrSkaterOrCoach = hasSkaters || isApprovedCoach || persona === 'parent' || persona === 'adult_skater' || persona === 'adult_skater_parent';

  // Helper function to get icon component from string name
  const getIconComponent = (iconName) => {
    const icons = {
      Person: PersonIcon,
      PersonAdd: PersonAddIcon,
      Group: GroupIcon,
    };
    return icons[iconName] || GroupIcon;
  };

  const toggleAdminMenu = () => setAdminMenuOpen(!adminMenuOpen);
  const toggleSeriesMenu = () => setSeriesMenuOpen(!seriesMenuOpen);
  const toggleInterclubMenu = () => setInterclubMenuOpen(!interclubMenuOpen);

  useEffect(() => {
    const loadBrandingAndSettings = async () => {
      try {
        // Check domain-based branding first
        const domainBranding = getDomainBranding();
        
        if (shouldUseDefaultBranding()) {
          // Use default branding for main domain (interclubseries.com)
          const defaultTitle = domainBranding.brandingTitle || 'Interclub Competition Manager';
          setBrandingTitle(defaultTitle);
          document.title = defaultTitle;
          setBrandingLogoUrl(''); // Use default logo
          
          // Still need to check qualifiers setting from interclub
          const baseUrl = await getApiBaseUrlWithFallback();
          const seasonsResponse = await fetch(`${baseUrl}/seasons?current=true`);
          if (seasonsResponse.ok) {
            const seasons = await seasonsResponse.json();
            const currentSeason = seasons.find((season) => season.isCurrent) || seasons[0];
            if (currentSeason?.interclubId) {
              const interclubResponse = await fetch(`${baseUrl}/interclubs/${currentSeason.interclubId}`);
              if (interclubResponse.ok) {
                const interclub = await interclubResponse.json();
                setQualifiersEnabled(interclub?.enableQualifiers !== false);
              }
            }
          }
          return;
        }
        
        // Load branding from interclub (for subdomains like pnic.interclubseries.com)
        const baseUrl = await getApiBaseUrlWithFallback();
        const seasonsResponse = await fetch(`${baseUrl}/seasons?current=true`);
        if (!seasonsResponse.ok) return;
        const seasons = await seasonsResponse.json();
        const currentSeason = seasons.find((season) => season.isCurrent) || seasons[0];
        if (!currentSeason) return;
        if (currentSeason.interclubId) {
          const interclubResponse = await fetch(`${baseUrl}/interclubs/${currentSeason.interclubId}`);
          if (interclubResponse.ok) {
            const interclub = await interclubResponse.json();
            const title = interclub?.brandingTitle || currentSeason.name || 'Interclub Competition Manager';
            setBrandingTitle(title);
            document.title = title;
            if (interclub?.logoUrl) {
              setBrandingLogoUrl(interclub.logoUrl);
            }
            // Check if qualifiers are enabled for this interclub
            setQualifiersEnabled(interclub?.enableQualifiers !== false);
            return;
          }
        }
        if (currentSeason.name) {
          setBrandingTitle(currentSeason.name);
          document.title = currentSeason.name;
        } else {
          document.title = 'Interclub Competition Manager';
        }
      } catch (error) {
        console.error('Failed to load branding:', error);
      }
    };
    loadBrandingAndSettings();
  }, []);

  const mainNavItems = [
    { text: 'Home', icon: HomeIcon, path: '/' },
    ...(shouldShowQualifierLink ? [{ text: 'Qualifiers', icon: QualifiersIcon, path: '/qualifiers' }] : []),
    { text: 'Competitions', icon: CompetitionsIcon, path: '/competitions' },
    { text: 'Rules', icon: RulesIcon, path: '/rules' },
  ];

  // User-specific navigation items with dynamic labels (only show when logged in)
  // For interclub admins who aren't parents/coaches, hide the skater management links (they use interclub admin instead)
  const SkatersIconComponent = getIconComponent(navLabels.skatersIcon);
  const showSkaterManagement = !hasInterclubAdminAccess || isParentOrSkaterOrCoach;
  
  const userNavItems = user ? [
    ...(showSkaterManagement ? [{ text: 'Dashboard', icon: HomeIcon, path: '/dashboard' }] : []),
    ...(showSkaterManagement ? [{ text: navLabels.skaters, icon: SkatersIconComponent, path: '/skaters' }] : []),
    ...(showSkaterManagement ? [{ text: navLabels.registration, icon: HowToRegIcon, path: '/registration' }] : []),
    { text: navLabels.profile, icon: PersonIcon, path: '/profile' },
  ] : [];

  // App Admin nav items (for users in adminUsers collection - full system access)
  const appAdminNavItems = [
    { text: 'Overview', icon: SettingsIcon, path: '/admin' },
    { text: 'All Users', icon: GroupIcon, path: '/admin/users' },
    { text: 'All Role Approvals', icon: RoleApprovalsIcon, path: '/admin/role-approvals' },
    { text: 'All Interclubs & Clubs', icon: GroupIcon, path: '/admin/interclubs' },
    { text: 'All Competitions', icon: AssessmentIcon, path: '/admin/competitions' },
    { text: 'All Skaters', icon: GroupIcon, path: '/admin/skaters' },
    { text: 'Score Lookup', icon: SearchIcon, path: '/admin/score-lookup' },
    { text: 'File Upload', icon: UploadIcon, path: '/admin/upload' },
    { text: 'File History', icon: StorageIcon, path: '/admin/files' },
    { text: 'Synced Coaches', icon: GroupIcon, path: '/admin/coaches' },
    { text: 'Data Tools', icon: SettingsIcon, path: '/admin/tools' },
    { text: 'App Settings', icon: SettingsIcon, path: '/admin/settings' },
  ];

  // My Series: series-level view (dashboard, competitions, billing, series settings)
  const seriesNavItems = [
    { text: 'Dashboard', icon: GroupIcon, path: '/interclub-admin' },
    { text: 'Competitions', icon: AssessmentIcon, path: '/interclub-admin/competitions' },
    { text: 'Stripe & Billing', icon: SettingsIcon, path: '/interclub-admin/billing' },
    { text: 'Data Tools', icon: SettingsIcon, path: '/interclub-admin/tools' },
    { text: 'Series Settings', icon: SettingsIcon, path: '/interclub-admin/settings' },
  ];
  // My Interclub: interclub-specific (clubs, skaters, coaches, users, interclub settings)
  const interclubNavItems = [
    { text: 'Dashboard', icon: GroupIcon, path: '/interclub-admin/interclub' },
    { text: 'Clubs', icon: GroupIcon, path: '/interclub-admin/interclub/clubs' },
    { text: 'Skaters', icon: GroupIcon, path: '/interclub-admin/interclub/skaters' },
    { text: 'Coaches', icon: GroupIcon, path: '/interclub-admin/interclub/coaches' },
    { text: 'Users', icon: GroupIcon, path: '/interclub-admin/interclub/users' },
    { text: 'Role Approvals', icon: RoleApprovalsIcon, path: '/interclub-admin/interclub/role-approvals' },
    { text: 'Interclub Settings', icon: SettingsIcon, path: '/interclub-admin/interclub/settings' },
  ];

  const handleNavigationWithCollapse = (path) => {
    if (isMobile) {
      toggleSidebar();
    }
    navigate(path);
  };

  const isActivePath = (path) => {
    if (path === '/') {
      return location.pathname === '/';
    }
    // For admin paths, only highlight if it's an exact match or the specific admin section
    if (path.startsWith('/admin')) {
      // If we're on a specific admin page, only highlight that exact path
      if (location.pathname.startsWith('/admin')) {
        // For admin overview, only highlight if we're exactly on /admin or /admin/
        if (path === '/admin' || path === '/admin/') {
          return location.pathname === '/admin' || location.pathname === '/admin/';
        }
        // For other admin paths, check if it's the exact match
        return location.pathname === path;
      }
      return false;
    }
    // For interclub-admin paths, same logic
    if (path.startsWith('/interclub-admin')) {
      if (location.pathname.startsWith('/interclub-admin')) {
        if (path === '/interclub-admin' || path === '/interclub-admin/') {
          return location.pathname === '/interclub-admin' || location.pathname === '/interclub-admin/';
        }
        return location.pathname === path;
      }
      return false;
    }
    return location.pathname.startsWith(path);
  };

  return (
    <>
      {/* Sidebar */}
      <Drawer
        variant={isMobile ? "temporary" : "permanent"}
        open={sidebarOpen}
        onClose={isMobile ? () => toggleSidebar() : undefined}
        sx={{
          flexShrink: 0,
          '& .MuiDrawer-paper': {
            width: sidebarOpen ? 280 : 70,
            boxSizing: 'border-box',
            backgroundColor: '#ffffff',
            borderRight: '1px solid #e0e0e0',
            position: 'fixed',
            left: 0,
            top: 0, // Start from top of screen
            height: '100vh', // Full height
            zIndex: theme.zIndex.drawer + 2,
            pointerEvents: 'auto',
            boxShadow: '0 2px 4px rgba(0,0,0,0.1)', // Add shadow to cover both sidebar and top bar
            transition: theme.transitions.create('width', {
              easing: theme.transitions.easing.sharp,
              duration: theme.transitions.duration.enteringScreen,
            }),
          },
        }}
      >
        <Box sx={{ 
          display: 'flex', 
          flexDirection: 'column', 
          height: '100%',
          overflow: 'hidden',
          width: '100%'
        }}>
          {/* Sidebar Header */}
          <Box sx={{ p: 2, borderBottom: '1px solid #e0e0e0' }}>
            <Box sx={{ 
              display: 'flex', 
              alignItems: 'center', 
              justifyContent: sidebarOpen ? 'flex-start' : 'center',
              cursor: 'pointer'
            }} onClick={() => handleNavigationWithCollapse('/')}>
              <img
                src={brandingLogoUrl || `${process.env.PUBLIC_URL}/logo192.png`}
                alt={brandingTitle ? `${brandingTitle} Logo` : 'Interclub Logo'}
                onError={(event) => {
                  if (event.currentTarget.src.includes('/logo192.png')) return;
                  event.currentTarget.src = `${process.env.PUBLIC_URL}/logo192.png`;
                }}
                style={{
                  height: '40px',
                  width: '40px',
                  borderRadius: '8px',
                  objectFit: 'cover'
                }}
              />
            </Box>
          </Box>

          {/* Impersonation Banner in Sidebar */}
          {isImpersonating && (
            <Box sx={{ 
              p: 1.5, 
              bgcolor: 'error.main', 
              color: 'white',
              borderBottom: '1px solid',
              borderColor: 'error.dark',
            }}>
              {sidebarOpen ? (
                <>
                  <Typography variant="caption" sx={{ fontWeight: 700, display: 'block', mb: 0.5 }}>
                    IMPERSONATING
                  </Typography>
                  <Typography variant="body2" sx={{ fontWeight: 600, mb: 1 }}>
                    {impersonatedUser?.displayName || impersonatedUser?.email || 'User'}
                  </Typography>
                  <Button
                    fullWidth
                    size="small"
                    variant="contained"
                    startIcon={<ExitIcon />}
                    onClick={stopImpersonation}
                    sx={{
                      bgcolor: 'white',
                      color: 'error.main',
                      fontWeight: 700,
                      '&:hover': { bgcolor: 'grey.100' },
                    }}
                  >
                    Exit
                  </Button>
                </>
              ) : (
                <Button
                  size="small"
                  onClick={stopImpersonation}
                  sx={{
                    minWidth: 'auto',
                    p: 1,
                    bgcolor: 'white',
                    color: 'error.main',
                    '&:hover': { bgcolor: 'grey.100' },
                  }}
                  title="Exit Impersonation"
                >
                  <ExitIcon />
                </Button>
              )}
            </Box>
          )}

          {/* Navigation Items - Scrollable */}
          <Box sx={{ 
            flexGrow: 1, 
            overflow: 'auto',
            width: '100%',
            minWidth: 0
          }}>
            {/* Main Navigation */}
            <List sx={{ px: 1, py: 1 }}>
              {mainNavItems.map((item) => (
                <ListItem key={item.path} disablePadding sx={{ mb: 0.5 }}>
                  <ListItemButton
                    onClick={() => handleNavigationWithCollapse(item.path)}
                    selected={isActivePath(item.path)}
                    sx={{
                      borderRadius: '8px',
                      minHeight: 48,
                      '&.Mui-selected': {
                        backgroundColor: 'primary.dark', // Darker blue for better contrast
                        color: 'white', // White text for better readability
                        '&:hover': {
                          backgroundColor: 'primary.dark',
                        },
                      },
                      '&:hover': {
                        backgroundColor: 'grey.100',
                      },
                    }}
                  >
                    <ListItemIcon
                      sx={{
                        color: isActivePath(item.path) ? 'white' : 'inherit', // White icons for active items
                        minWidth: 40
                      }}
                    >
                      <item.icon />
                    </ListItemIcon>
                    {sidebarOpen && (
                      <ListItemText 
                        primary={item.text}
                        primaryTypographyProps={{
                          fontSize: '0.85rem',
                          fontWeight: isActivePath(item.path) ? 600 : 400
                        }}
                      />
                    )}
                  </ListItemButton>
                </ListItem>
              ))}
            </List>

            {/* User Navigation - Only show when logged in */}
            {user && userNavItems.length > 0 && (
              <>
                <Divider sx={{ my: 2, mx: 2 }} />
                <List sx={{ px: 1, py: 1 }}>
                  {userNavItems.map((item) => (
                    <ListItem key={item.path} disablePadding sx={{ mb: 0.5 }}>
                      <ListItemButton
                        onClick={() => handleNavigationWithCollapse(item.path)}
                        selected={isActivePath(item.path)}
                        sx={{
                          borderRadius: '8px',
                          minHeight: 48,
                          '&.Mui-selected': {
                            backgroundColor: 'primary.dark',
                            color: 'white',
                            '&:hover': {
                              backgroundColor: 'primary.dark',
                            },
                          },
                          '&:hover': {
                            backgroundColor: 'grey.100',
                          },
                        }}
                      >
                        <ListItemIcon
                          sx={{
                            color: isActivePath(item.path) ? 'white' : 'inherit',
                            minWidth: 40
                          }}
                        >
                          <item.icon />
                        </ListItemIcon>
                        {sidebarOpen && (
                          <ListItemText 
                            primary={item.text}
                            primaryTypographyProps={{
                              fontSize: '0.85rem',
                              fontWeight: isActivePath(item.path) ? 600 : 400
                            }}
                          />
                        )}
                      </ListItemButton>
                    </ListItem>
                  ))}
                </List>
              </>
            )}

            {/* My Series + My Interclub (for approved interclub admins) */}
            {hasInterclubAdminAccess && (
              <>
                <Divider sx={{ my: 2, mx: 2 }} />

                {/* My Series */}
                <List sx={{ px: 1, py: 1 }}>
                  <ListItem disablePadding>
                    <ListItemButton
                      onClick={toggleSeriesMenu}
                      sx={{
                        borderRadius: '8px',
                        minHeight: 48,
                        backgroundColor: seriesMenuOpen ? 'primary.50' : 'transparent',
                        '&:hover': { backgroundColor: 'primary.100' },
                      }}
                    >
                      <ListItemIcon sx={{ minWidth: 40, color: 'primary.main' }}>
                        <AdminIcon />
                      </ListItemIcon>
                      {sidebarOpen && (
                        <>
                          <ListItemText
                            primary="My Series"
                            primaryTypographyProps={{ fontSize: '0.85rem', fontWeight: 600, color: 'primary.main' }}
                          />
                          {seriesMenuOpen ? <ExpandLess /> : <ExpandMore />}
                        </>
                      )}
                    </ListItemButton>
                  </ListItem>
                </List>
                <Collapse in={seriesMenuOpen} timeout="auto" unmountOnExit>
                  <List component="div" disablePadding>
                    {seriesNavItems.map((item) => (
                      <ListItem key={item.path} disablePadding>
                        <ListItemButton
                          onClick={() => handleNavigationWithCollapse(item.path)}
                          selected={isActivePath(item.path)}
                          sx={{
                            pl: sidebarOpen ? 4 : 2,
                            borderRadius: '8px',
                            mx: 1,
                            minHeight: 48,
                            '&.Mui-selected': { backgroundColor: 'primary.dark', color: 'white', '&:hover': { backgroundColor: 'primary.dark' } },
                            '&:hover': { backgroundColor: 'grey.100' },
                          }}
                        >
                          <ListItemIcon sx={{ color: isActivePath(item.path) ? 'white' : 'inherit', minWidth: 40 }}>
                            <item.icon />
                          </ListItemIcon>
                          {sidebarOpen && (
                            <ListItemText primary={item.text} primaryTypographyProps={{ fontSize: '0.85rem', fontWeight: isActivePath(item.path) ? 600 : 400 }} />
                          )}
                        </ListItemButton>
                      </ListItem>
                    ))}
                  </List>
                </Collapse>

                {/* My Interclub */}
                <List sx={{ px: 1, py: 1 }}>
                  <ListItem disablePadding>
                    <ListItemButton
                      onClick={toggleInterclubMenu}
                      sx={{
                        borderRadius: '8px',
                        minHeight: 48,
                        backgroundColor: interclubMenuOpen ? 'primary.50' : 'transparent',
                        '&:hover': { backgroundColor: 'primary.100' },
                      }}
                    >
                      <ListItemIcon sx={{ minWidth: 40, color: 'primary.main' }}>
                        <GroupIcon />
                      </ListItemIcon>
                      {sidebarOpen && (
                        <>
                          <ListItemText
                            primary="My Interclub"
                            primaryTypographyProps={{ fontSize: '0.85rem', fontWeight: 600, color: 'primary.main' }}
                          />
                          {interclubMenuOpen ? <ExpandLess /> : <ExpandMore />}
                        </>
                      )}
                    </ListItemButton>
                  </ListItem>
                </List>
                <Collapse in={interclubMenuOpen} timeout="auto" unmountOnExit>
                  <List component="div" disablePadding>
                    {interclubNavItems.map((item) => (
                      <ListItem key={item.path} disablePadding>
                        <ListItemButton
                          onClick={() => handleNavigationWithCollapse(item.path)}
                          selected={isActivePath(item.path)}
                          sx={{
                            pl: sidebarOpen ? 4 : 2,
                            borderRadius: '8px',
                            mx: 1,
                            minHeight: 48,
                            '&.Mui-selected': { backgroundColor: 'primary.dark', color: 'white', '&:hover': { backgroundColor: 'primary.dark' } },
                            '&:hover': { backgroundColor: 'grey.100' },
                          }}
                        >
                          <ListItemIcon sx={{ color: isActivePath(item.path) ? 'white' : 'inherit', minWidth: 40 }}>
                            <item.icon />
                          </ListItemIcon>
                          {sidebarOpen && (
                            <ListItemText primary={item.text} primaryTypographyProps={{ fontSize: '0.85rem', fontWeight: isActivePath(item.path) ? 600 : 400 }} />
                          )}
                        </ListItemButton>
                      </ListItem>
                    ))}
                  </List>
                </Collapse>
              </>
            )}

            {/* App Admin Section (for users in adminUsers collection) */}
            {hasAppAdminAccess && (
              <>
                <Divider sx={{ my: 2, mx: 2 }} />
                
                {/* App Admin Menu Header */}
                <List sx={{ px: 1, py: 1 }}>
                  <ListItem disablePadding>
                    <ListItemButton
                      onClick={toggleAdminMenu}
                      sx={{
                        borderRadius: '8px',
                        minHeight: 48,
                        backgroundColor: adminMenuOpen ? 'error.50' : 'transparent',
                        '&:hover': {
                          backgroundColor: 'error.100',
                        },
                      }}
                    >
                      <ListItemIcon sx={{ minWidth: 40, color: 'error.main' }}>
                        <AdminIcon />
                      </ListItemIcon>
                      {sidebarOpen && (
                        <>
                          <ListItemText 
                            primary="App Admin"
                            primaryTypographyProps={{
                              fontSize: '0.85rem',
                              fontWeight: 600,
                              color: 'error.main'
                            }}
                          />
                          {adminMenuOpen ? <ExpandLess /> : <ExpandMore />}
                        </>
                      )}
                    </ListItemButton>
                  </ListItem>
                </List>

                {/* App Admin Submenu */}
                <Collapse in={adminMenuOpen} timeout="auto" unmountOnExit>
                  <List component="div" disablePadding>
                    {appAdminNavItems.map((item) => (
                      <ListItem key={item.path} disablePadding>
                        <ListItemButton
                          onClick={() => handleNavigationWithCollapse(item.path)}
                          selected={isActivePath(item.path)}
                          sx={{
                            pl: sidebarOpen ? 4 : 2,
                            borderRadius: '8px',
                            mx: 1,
                            minHeight: 48,
                            '&.Mui-selected': {
                              backgroundColor: 'error.dark',
                              color: 'white',
                              '&:hover': {
                                backgroundColor: 'error.dark',
                              },
                            },
                            '&:hover': {
                              backgroundColor: 'grey.100',
                            },
                          }}
                        >
                          <ListItemIcon
                            sx={{
                              color: isActivePath(item.path) ? 'white' : 'inherit',
                              minWidth: 40
                            }}
                          >
                            <item.icon />
                          </ListItemIcon>
                          {sidebarOpen && (
                            <ListItemText 
                              primary={item.text}
                              primaryTypographyProps={{
                                fontSize: '0.85rem',
                                fontWeight: isActivePath(item.path) ? 600 : 400
                              }}
                            />
                          )}
                        </ListItemButton>
                      </ListItem>
                    ))}
                  </List>
                </Collapse>
              </>
            )}
          </Box>

          {/* Sidebar Footer with Expand Button */}
          <Box sx={{ 
            p: 2, 
            borderTop: '1px solid #e0e0e0',
            display: 'flex',
            justifyContent: 'center'
          }}>
            {!isMobile && (
              <Box
                onClick={toggleSidebar}
                sx={{
                  width: 32,
                  height: 32,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  cursor: 'pointer',
                  borderRadius: '50%',
                  backgroundColor: 'grey.100',
                  '&:hover': {
                    backgroundColor: 'grey.200',
                  },
                }}
              >
                {sidebarOpen ? <ChevronLeft fontSize="small" /> : <ChevronRight fontSize="small" />}
              </Box>
            )}
          </Box>
        </Box>
      </Drawer>
      
      {/* Spacer to prevent content from being hidden behind fixed sidebar */}
      {!isMobile && (
        <Box sx={{ width: sidebarOpen ? 280 : 70, flexShrink: 0 }} />
      )}
    </>
  );
}